import type { ConnectorDownload, ConnectorDownloads } from "../../api/connectorReleasesApi";

/**
 * "Connect this PC", the one link the DMW Connector app on the person's PC answers (plan CI-4.3):
 * the app asks them to confirm the server and, with the account's ticket (F122), that account;
 * then it pairs (CN-2), at once with the ticket, else approved in the browser. Only before
 * pairing: once a PC is paired, the server asks its connector directly (F85). Without the app,
 * nothing happens.
 */
export function connectorPairingLink(serverOrigin: string, ticket: string | null): string {
  const server = `dmw-connector://pair?server=${encodeURIComponent(serverOrigin)}`;
  return ticket === null ? server : `${server}&ticket=${encodeURIComponent(ticket)}`;
}

/** The system the browser runs on, as the release job names systems; `null` when it can't tell. */
export function browserSystem(userAgent: string): string | null {
  if (/Windows/i.test(userAgent)) return "windows";
  if (/Mac OS X|Macintosh/i.test(userAgent)) return "darwin";
  if (/Linux|X11/i.test(userAgent) && !/Android/i.test(userAgent)) return "linux";
  return null;
}

/** What a system is offered first: the install command, its one installer, or nothing yet. */
export type SystemOffer =
  | { readonly kind: "command"; readonly command: string }
  | { readonly kind: "installer"; readonly download: ConnectorDownload }
  | { readonly kind: "none" };

/** The line people paste into a terminal on Linux or macOS (docs/connector-release.md RE-7). */
export function installCommand(installScript: string): string {
  return `curl -fsSL ${installScript} | sh`;
}

/**
 * The offer for this browser's system (F47). Where the release has a package for it (Linux,
 * macOS) and the server serves the install script, the command: the script picks the CPU,
 * which a browser can't tell (Safari on Apple Silicon reports an Intel Mac). Otherwise the
 * system's installer, if it has exactly one; of several, none is guessed.
 */
export function offerFor(offered: Pick<ConnectorDownloads, "downloads" | "installScript">, system: string | null): SystemOffer {
  const forSystem = offered.downloads.filter((download) => download.os === system);
  if (offered.installScript !== null && forSystem.some((download) => download.kind === "package")) {
    return { kind: "command", command: installCommand(offered.installScript) };
  }
  const [only, ...more] = forSystem.filter((download) => download.kind === "installer");
  return only !== undefined && more.length === 0 ? { kind: "installer", download: only } : { kind: "none" };
}

/**
 * The installers listed under "Other systems": the other systems' own. This system's are listed
 * there only where none was offered first, so a Mac offered the command isn't shown its app as
 * another system's.
 */
export function otherSystemsInstallers(downloads: readonly ConnectorDownload[], system: string | null, offer: SystemOffer): ConnectorDownload[] {
  return downloads.filter((download) => download.kind === "installer" && (download.os !== system || offer.kind === "none"));
}
